import React, {useContext, useEffect, useMemo, useState} from 'react';
import {UserContext} from "@context/UserContext";
import axios from "axios";
import {ContentContext} from "@context/ContentContext";
import QuizzesLegend, {QuizMarkColor} from "./legends/QuizzesLegend";
import {BrandContext} from "@context/BrandContext";
import AchievementStar, {STAR} from "./AchievementStar";
import QuizQuestionsLegend from "./legends/QuizQuestionsLegend";
import StudentProgress from "./StudentProgress";

const StudentProgressAchievement = ()=>{

    const { user } = useContext(UserContext),
        {content} = useContext(ContentContext),
        brandConfig = useContext(BrandContext),
        [error, setError] = useState(''),
        [responseData, setResponseData] = useState({}),
        [topicsArray, setTopicsArray] = useState([]),
        [loading, setLoading] = useState(false),
        [hasQuestionClusters,setHasQuestionClusters] = useState(false);

    const makeColumn = ({key, value, children, render, align = 'center', ...rest})=>{
            const obj = { title:value, dataIndex:key, key:key,
                align: align, ...rest};

        if(children){
            obj.children = children;
        }
        if(render){
            obj.render = render;
        }
        return obj;
    }

    const questionLevelChildren = [];
    for(let i = 0; i < brandConfig.questionLevels; i++){
        const levelObj = {key:'level'+(i+1)+'Star', value:brandConfig.questionLevelNames[i],
            render:(_,data)=>{
            if(data.ignoreQuizQuestions){
                return '';
            }
            const star = data['level'+(i+1)+'Star'];
            return <AchievementStar style={{justifySelf:'center'}} starFill={star.starFill} starColor={star.color} altText={'Quiz level '+(i+1)+' '+star.ariaLabel} />
            }
        }
        questionLevelChildren.push(makeColumn(levelObj))
    }

    const columns = useMemo(()=>{
        const cols = [
            makeColumn({key:'section',value:brandConfig.conceptName, align:'left'}),
            makeColumn({key:'walkthroughs',value:brandConfig.walkthroughsName}),
            makeColumn({key:'quizQuestions',value:brandConfig.questionsName,
                children:questionLevelChildren
            })];
        if(hasQuestionClusters){
            cols.push(makeColumn({key:'quizzes',value:'Quizzes',render:(obj, data)=>{
                    if(!data.ignoreQuizQuestions){
                        return undefined;
                    }
                    const value = obj.markPercentage,
                        quizAnswered = obj.hasOwnProperty('answerDate');
                    let color,
                        className = '';
                    if(!quizAnswered){
                        color = QuizMarkColor.notComplete.color;
                        className = QuizMarkColor.notComplete.className;
                    }else if(value < 30){
                        color = QuizMarkColor.mark0to29.color;
                        className = QuizMarkColor.mark0to29.className;
                    }else if(value < 90){
                        color = QuizMarkColor.mark30to89.color;
                        className = QuizMarkColor.mark30to89.className;
                    }else if(value <= 100){
                        color = QuizMarkColor.mark90to100.color;
                        className = QuizMarkColor.mark90to100.className;
                    }

                    return (<div className={'flexColumn'} style={{position:'relative'}}>
                        {quizAnswered && <div aria-label={'Quiz attempt date'}>{user.dateFormat.format(obj.markDate)}</div>}
                        <div aria-label={quizAnswered?'Quiz not complete':'Quiz score'} className={className + ' quizScoreShape'} style={{backgroundColor:color, color:'#ffffff',display:'block'}}>{quizAnswered?value+'%':''}</div>
                    </div>)
                }}),);
        }
        cols.push(makeColumn({key:'scorcher',value:'Scorcher Last result'}));
        return cols;
    },[hasQuestionClusters])

    const fetchData =()=>{
        axios.get('/web/ui/report/student/achievementProgress')
            .then((response)=>{
                setResponseData(response.data);
            }).catch((error)=>{
                console.error('Error loading achievement progress: ',error);
                setError(error);
        }).finally(()=>{
            setLoading(false);
        })
    }

    useEffect(()=>{
        setLoading(true);
        fetchData();
    },[])

    useEffect(()=>{
        if(Object.keys(responseData).length > 0){
            modifyDataForTable(responseData);
        }
    },[responseData, hasQuestionClusters]);

    const ScorcherLastResult = (props)=>{
        const {score, maxScore, time, resultDate} = props;
        if(resultDate){
            return (<div>
                <div className={'flexCentre mediumGap flexWrap'}><div>{score}/{maxScore}</div><div>{time}</div><div>{user.dateFormat.format(resultDate)}</div></div>
            </div>)
        }
        return undefined;

    }

    const modifyDataForTable = (responseData)=>{

        const topicMap = new Map();
        responseData.reportData.forEach((concept)=>{

            const topicIdKey = String(concept.topicId),
                conceptRow =
                    {section: concept.conceptName,
                        walkthroughs: concept.walkthroughCount > 0 ? concept.walkthroughCount : '',
                        quizzes: concept.questionClusterCount > 0 ? concept.questionClusterResults[concept.questionClusterCount-1] : '',
                        scorcher: concept.scorcherResultDate && <ScorcherLastResult score={concept.scorcherScore} maxScore={concept.maxScorcherScore} time={concept.scorcherTime} resultDate={concept.scorcherResultDate}  />
                    };
            let starType;
            const addToIndex = brandConfig.questionLevels === 3 ? 2:1;
            for(let i = 0; i < brandConfig.questionLevels; i++){
                if(concept['level'+(i+addToIndex)+'Achieved'] && concept['level'+(i+addToIndex)+'Achieved'] === 1){
                    starType = STAR.ACHIEVED;
                }else if((concept['successes'+(i+addToIndex)] + concept['failures'+(i+addToIndex)]) > 0){
                    starType = STAR.ATTEMPTED;
                }else{
                    starType = STAR.NOT_ATTEMPTED;
                }
                conceptRow['level'+(i+1)+'Star'] = {...starType};
            }
            conceptRow.ignoreQuizQuestions = concept.hasQuestionCluster;
            if(!hasQuestionClusters && concept.hasQuestionCluster){
                setHasQuestionClusters(true);
            }

            if(topicMap.has(topicIdKey)){
                topicMap.get(topicIdKey).items.push(conceptRow);
            }else{
                topicMap.set(topicIdKey,{id:concept.topicId, title:concept.topicName, items:[conceptRow], columns: columns});
            }
        })
        setTopicsArray([...topicMap.values()]);
    }

    const legends = useMemo(()=>{
        const legendArr = [<QuizQuestionsLegend heading={brandConfig.questionsName} />];
        if(hasQuestionClusters){
            legendArr.push(<QuizzesLegend heading={brandConfig.quizzesName} />);
        }
        return legendArr;
    },[hasQuestionClusters])

    return (
        <>
            <StudentProgress
                loading={loading}
                legends={legends}
                columns={columns}
                dataArray={topicsArray}
                defaultActiveKey={content.currentTopic?.topicId}
            />
        </>
    )
}



export default StudentProgressAchievement;